<script setup lang="ts">
import { Image } from '@/components';
import { type FabricStore, getStyle3dFabricApi } from '@/api';
import { useRoute, useRouter } from 'vue-router';
import { useParamStore } from '@/store/param.ts';
import { useFabricStore } from '@/store/fabric.ts';
import { storeToRefs, useRelationStore, useSelectionStore, useStyle3dStore } from '@repo/stores';
import { type FabricShareInfo, useStyle3d } from '@repo/style3d';
import { joinUrl } from '@repo/utils';
import axios, { type AxiosResponse } from 'axios';
import { generateFabricInfo, getAppConfig } from '@/utils';

const props = defineProps<{ fabrics: FabricStore[] }>();

const router = useRouter();
const route = useRoute();
const { refreshLoadResourceByRoute } = useParamStore();

const selectionStore = useSelectionStore();
const relationStore = useRelationStore();
const paramStore = useParamStore();
const fabricStore = useFabricStore();

const style3dStore = useStyle3dStore();
const { resolution } = storeToRefs(style3dStore);

const { OSS_BASE_URL, INDIVIDUAL_FABRIC_SHARES } = getAppConfig();
const fabricRootPath = `${OSS_BASE_URL}${INDIVIDUAL_FABRIC_SHARES}`;

const { loadFabric } = useStyle3d();

const onClick = async (item: FabricStore) => {
  const { selectedFabricId } = selectionStore;
  const { query, params } = route;
  const { scoResource } = paramStore.resourceParam;
  // 兼容老数据，之前只存了id没有存fabricId
  const fabricId = item.fabricId || item.id;
  const originUrl = item.originUrl || '';
  
  console.log('[fabric-list] onClick:', {
    fabricId,
    selectedFabricId,
    currentParams: params,
    currentQuery: query
  });
  
  if (selectedFabricId === '' || selectedFabricId === null) {
    await router.replace({
      params: { type: item.resourceType, id: fabricId },
      query: { ...query, originUrl: btoa(originUrl), color: undefined, colors: undefined }
    });
    await refreshLoadResourceByRoute();
  } else {
    let fabricShareInfo: Omit<FabricShareInfo, 'mask' | 'metaData' | 'doc' | 'enterprise_id'>;
    if (item.resourceType === 'style3d') {
      const style3dFabric = await getStyle3dFabricApi(fabricId);
      const { xhr_path, thumb_path, width, height } = style3dFabric.data;
      const generateRibInfo = scoResource.type !== 'style3d';

      fabricShareInfo = await generateFabricInfo(
        {
          info: { width, height },
          thumb: thumb_path,
          xhr_path: xhr_path
        },
        generateRibInfo
      );
    } else {
      const fabricRoot = joinUrl(fabricRootPath, fabricId);
      const infoResult: AxiosResponse<FabricShareInfo> = await axios.get(joinUrl(fabricRoot, 'info.json'));
      fabricShareInfo = infoResult.data;
    }

    const found = relationStore.shapeList.find(item => item.id === selectedFabricId);
    const materialIndex = found?.materialIndex ?? 0;
    
    console.log('[fabric-list] 更新面料:', {
      selectedFabricId,
      materialIndex,
      fabricId,
      foundShape: found
    });

    const filteredList = relationStore.shapeList.filter(item => {
      const itemMaterialIndex = item.materialIndex ?? 0;
      return itemMaterialIndex === materialIndex;
    });
    
    const updatedList = filteredList.map(item => {
      const fabricInfoType = item.type ? item.type : 'main';
      return {
        ...item,
        ...fabricShareInfo.info[fabricInfoType] ?? fabricShareInfo.info['main'],
        color:"" //清空变色的颜色
      };
    });

    const fabric = {
      id: item.id,
      type: item.resourceType,
      originUrl: item.originUrl
    };
    if (item.resourceType === 'style3d') {
      const shapes = await loadFabric(updatedList, { fabricRootPath: '', resolution: resolution.value, fabric });
      relationStore.updateShape(shapes);
    } else {
      const fabricRoot = joinUrl(fabricRootPath, fabricId);
      const shapes = await loadFabric(updatedList, {
        fabricRootPath: fabricRoot,
        resolution: resolution.value,
        fabric
      });
      relationStore.updateShape(shapes);
    }
    
    // 更新URL中对应materialIndex的面料ID
    const currentParams = params as any;
    
    // 构建新的路由参数，确保面料ID顺序正确
    const newParams = { ...currentParams };
    const newQuery: any = { ...query, originUrl: btoa(originUrl) };
    
    // 处理颜色参数：只清空对应materialIndex的颜色
    if (query.colors) {
      try {
        const colorsObj = JSON.parse(decodeURIComponent(query.colors as string));
        // 删除对应materialIndex的颜色
        delete colorsObj[materialIndex];
        // 如果还有其他颜色，保留colors参数，否则删除
        if (Object.keys(colorsObj).length > 0) {
          newQuery.colors = encodeURIComponent(JSON.stringify(colorsObj));
        } else {
          newQuery.colors = undefined;
        }
      } catch (e) {
        console.error('[fabric-list] 解析colors参数失败:', e);
        newQuery.colors = undefined;
      }
    }
    
    // 如果是materialIndex=0且使用的是旧的color参数，清空它
    if (materialIndex === 0 && query.color) {
      newQuery.color = undefined;
    }
    
    console.log('[fabric-list] 更新路由前:', {
      materialIndex,
      fabricId,
      currentParams,
      '清空颜色': materialIndex
    });
    
    // 更新fabricStore中对应materialIndex的面料信息
    try {
      let fullFabricInfo;
      if (item.resourceType === 'style3d') {
        const style3dFabric = await getStyle3dFabricApi(fabricId);
        const xhr_path = style3dFabric.data.xhr_path;
        const thumb = style3dFabric.data.thumb_path;

        const keyMap = style3dFabric.create_info.fields.map(field => ({ name: field.name, key: field.key }));
        const fabricProperty = keyMap
          .map((field) => ({ name: field.name, key: field.key, value: style3dFabric.data[field.key] }))
          .filter(field => field.value);

        const carousels = style3dFabric.data?.all_thumbs?.map((thumbItem: any) => {
          const url = thumbItem.thumb_path.split('?')[0];
          const urlParts = url.split('/');
          const decodedFileName = decodeURIComponent(urlParts[urlParts.length - 1]);
          const fileType = decodedFileName?.split('.')?.pop()?.toLowerCase() || '';

          const fileCategory = ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileType) ? 'picture' :
            ['mp4', 'avi', 'mov', 'wmv'].includes(fileType) ? 'video' : 'unknown';

          return {
            name: decodedFileName,
            type: fileCategory,
            url
          };
        });

        fullFabricInfo = {
          id: `style3d-${fabricId}`,
          originUrl: item.originUrl || '',
          fabricId: fabricId,
          info: {
            name: xhr_path,
            width: style3dFabric.data.width,
            height: style3dFabric.data.height
          },
          carousels,
          resourceType: 'style3d' as const,
          enterpriseId: item.enterpriseId || '',
          enterpriseName: item.enterpriseName || '',
          thumb,
          xhr_path,
          properties: fabricProperty,
          mask: {
            srcImage: undefined,
            colors: undefined,
            list: []
          }
        };
      } else {
        const fabricRoot = joinUrl(fabricRootPath, fabricId);
        const infoResult: AxiosResponse<FabricShareInfo> = await axios.get(joinUrl(fabricRoot, 'info.json'));
        const fullFabricShareInfo = infoResult.data;
        
        const thumbUrl = fullFabricShareInfo.info?.thumbnail
          ? joinUrl(fabricRoot, 'fabric', fullFabricShareInfo.info.thumbnail.name)
          : fullFabricShareInfo.info.main
            ? joinUrl(fabricRoot, 'fabric', fullFabricShareInfo.info.main.name)
            : item.thumb;
        
        fullFabricInfo = {
          id: item.id,
          originUrl: item.originUrl || '',
          fabricId: fabricId,
          info: fullFabricShareInfo.metaData.info,
          thumb: thumbUrl,
          xhr_path: undefined,
          enterpriseId: fullFabricShareInfo.metaData.enterpriseID || item.enterpriseId || '',
          enterpriseName: fullFabricShareInfo.metaData.enterpriseName || item.enterpriseName || '',
          resourceType: 'cloudknit' as const,
          properties: fullFabricShareInfo.metaData.property || item.properties || [],
          styleProperties: fullFabricShareInfo.metaData?.propertyStyle,
          carousels: fullFabricShareInfo.doc?.map(docItem => ({ ...docItem, url: joinUrl(fabricRoot, docItem.name) })) || [],
          logo: fullFabricShareInfo.metaData.logo,
          mask: {
            srcImage: fullFabricShareInfo.mask?.srcImage,
            colors: fullFabricShareInfo.mask?.colors,
            list: fullFabricShareInfo.mask?.list || []
          }
        };
      }
      
      // 更新对应materialIndex的fabricInfo
      await fabricStore.setFabricInfo(fullFabricInfo, materialIndex);
      
      // 检查当前选中的区域索引是否超出新面料的区域数量
      const currentSelectedIndex = fabricStore.getSelectedRegionIndex(materialIndex);
      const newRegionCount = fullFabricInfo.mask?.colors?.length || 0;
      
      if (newRegionCount > 0 && currentSelectedIndex >= newRegionCount) {
        // 如果当前选中的索引超出范围，重置为第一个区域
        fabricStore.setSelectedRegionIndex(0, materialIndex);
        console.log('[fabric-list] 重置选中区域索引:', {
          materialIndex,
          oldIndex: currentSelectedIndex,
          newIndex: 0,
          regionCount: newRegionCount
        });
      }
      
      console.log('[fabric-list] 已更新fabricStore:', {
        materialIndex,
        fabricId,
        hasMask: !!fullFabricInfo.mask?.srcImage,
        regionCount: newRegionCount,
        selectedRegionIndex: fabricStore.getSelectedRegionIndex(materialIndex)
      });
    } catch (error) {
      console.error('[fabric-list] 更新fabricStore失败:', error);
    }
    
    // 根据materialIndex更新对应的参数位置，保持其他位置不变
    if (materialIndex === 0) {
      newParams.id = fabricId;
      newParams.type = item.resourceType;
      // 保持 id1 和 id2 不变
    } else if (materialIndex === 1) {
      newParams.id1 = fabricId;
      // 保持 id 和 id2 不变
    } else if (materialIndex === 2) {
      newParams.id2 = fabricId;
      // 保持 id 和 id1 不变
    }
    
    // 统一使用replace更新路由，避免竞态条件
    console.log('[fabric-list] 更新路由:', {
      materialIndex,
      fabricId,
      newParams,
      newQuery,
      '验证': {
        'id保持': materialIndex !== 0 ? newParams.id === currentParams.id : true,
        'id1保持': materialIndex !== 1 ? newParams.id1 === currentParams.id1 : true,
        'id2保持': materialIndex !== 2 ? newParams.id2 === currentParams.id2 : true
      }
    });
    
    await router.replace({
      params: newParams,
      query: newQuery
    });
  }
};
</script>

<template>
  <div
    style="padding: 0 5px;"
    v-for="(item) in props.fabrics"
    :key="item.id"
  >
    <Image
      :width="85"
      :height="85"
      :src="item.thumb"
      fit="scale-down"
      @click="()=>onClick(item)"
    />
    <div class="fabric-name">
      {{ item.info ? item.info.name : '' }}
    </div>
  </div>
</template>

<style scoped>
.fabric-name {
  color: rgba(255, 255, 255, 0.92);
  font-size: 12px;
  text-align: center;
  margin-top: 6px;
  line-height: 1.2;
  max-height: 3.6em; /* allow up to 3 lines */
  width: 85px; /* match image width */
  margin-left: auto;
  margin-right: auto;
  overflow: hidden;
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  word-break: break-word;
  text-overflow: ellipsis;
  white-space: normal;
}
</style>